import { InstallmentStatus, PaymentPlanMode } from '@/lib/builder/models/payment-plan.model';

/**
 * Dashboard pagamenti (/payments).
 *
 * Tutto arriva da un'unica RPC `get_payments_dashboard`: KPI e liste devono riferirsi
 * allo stesso insieme di righe, e con due chiamate separate basterebbe un incasso fra
 * l'una e l'altra per mostrare un totale che non torna con la tabella sotto.
 *
 * Nessun campo qui e' ricalcolabile nel browser: `is_overdue` e `generated_on` sono
 * derivati dalla data italiana lato database (vedi itinerary_payment_summary), non da
 * `new Date()` che sarebbe UTC.
 */

/**
 * Chiave della query TanStack. Vive qui e non nel file degli hook perche' la invalida
 * anche `useMarkInstallmentPaid` del builder: importarla da li' creerebbe un ciclo.
 */
export const PAYMENTS_DASHBOARD_QUERY_KEY = 'payments-dashboard';

/**
 * Nessun limite in avanti: la RPC tratta qualsiasi valore <= 0 come "tutte le rate
 * pending", scadute comprese.
 */
export const DUE_WINDOW_ALL = 0;

/**
 * Finestra "in scadenza" offerta dalla UI, in giorni. Le prime tre ricalcano il selettore
 * di periodo della panoramica; l'ultima e' l'unica via per le rate lontane (una rata puo'
 * essere fissata a un mese dalla partenza e il viaggio essere fra nove mesi: a 90 giorni
 * non comparirebbe da nessuna parte).
 */
export const DUE_WINDOW_OPTIONS = [7, 30, 90, DUE_WINDOW_ALL] as const;

export type DueWindowDays = (typeof DUE_WINDOW_OPTIONS)[number];

export const DEFAULT_DUE_WINDOW: DueWindowDays = 30;

export interface IPaymentsKpis {
    itineraries_count: number;
    total_amount: number;
    paid_amount: number;
    remaining_amount: number;
    /** Somma delle rate pending con scadenza gia' passata. */
    overdue_amount: number;
    overdue_count: number;
    /** Rate pending dentro la finestra scelta, scadute escluse. */
    due_soon_amount: number;
    due_soon_count: number;
}

/** Una rata da incassare. Include le scadute: sono la stessa coda di lavoro. */
export interface IDueInstallment {
    installment_id: string;
    itinerary_id: string;
    itinerary_title: string;
    /** Chiavi per `people`: i dati della persona non viaggiano riga per riga. */
    client_key: string | null;
    designer_id: string | null;
    seq: number;
    /** Rate non annullate del piano, per la dicitura "rata 2 di 4". */
    installments_count: number;
    label: string | null;
    amount: number;
    due_date: string;
    /** Negativo se scaduta. Calcolato sulla data italiana. */
    days_to_due: number;
    is_overdue: boolean;
    /**
     * Addebito differito in volo (SEPA): il cliente ha pagato, Stripe non ha ancora
     * confermato. La rata resta in lista ma non e' ne' scaduta ne' da sollecitare.
     */
    payment_processing: boolean;
    currency: string;
}

/** Una rata gia' incassata, per lo storico recente. */
export interface IPaidInstallment {
    installment_id: string;
    itinerary_id: string;
    itinerary_title: string;
    client_key: string | null;
    designer_id: string | null;
    seq: number;
    installments_count: number;
    label: string | null;
    /** paid_amount se presente, altrimenti l'importo previsto della rata. */
    amount: number;
    paid_at: string | null;
    payment_method: string | null;
    currency: string;
}

/** Un viaggio con piano rate e il suo stato di incasso. */
export interface IPaymentsItinerary {
    itinerary_id: string;
    title: string;
    client_key: string | null;
    designer_id: string | null;
    start_date: string | null;
    end_date: string | null;
    itinerary_status: string | null;
    mode: PaymentPlanMode;
    currency: string;
    total_amount: number;
    paid_amount: number;
    remaining_amount: number;
    overdue_amount: number;
    installments_count: number;
    paid_count: number;
    all_paid: boolean;
    next_due_date: string | null;
    has_overdue: boolean;
}

/** Stato di impaginazione di una lista: la pagina mostrata e quante righe esistono in tutto. */
export interface IPaymentsPage {
    page: number;
    page_size: number;
    /**
     * Righe che soddisfano i filtri, non righe restituite. I KPI sono calcolati su questo
     * insieme e non sulla pagina: un totale che si muove cambiando pagina sarebbe un bug.
     */
    total_count: number;
}

/**
 * Una persona citata dalle liste: collaboratore o cliente.
 *
 * Sta nel dizionario `people` e non nelle righe perche' un collaboratore con quaranta
 * rate in scadenza comparirebbe quaranta volte con nome, email e URL della foto. Cosi'
 * la riga porta solo la chiave, e aggiungere un campo alla persona e' un punto solo.
 */
export interface IPaymentsPerson {
    name: string | null;
    email: string | null;
    avatar_url: string | null;
    kind: 'advisor' | 'client';
}

/**
 * Chiavi di `people`: UUID per i collaboratori, email normalizzata per i clienti.
 *
 * Il cliente NON ha un id — le richieste anonime non hanno `user_id` — ed e' il motivo
 * per cui le due chiavi convivono nello stesso dizionario con formati diversi. Non
 * possono collidere.
 */
export type PeopleDictionary = Record<string, IPaymentsPerson>;

/** Persona di una riga, o null se la chiave manca (cliente senza email, viaggio senza advisor). */
export const personOf = (
    people: PeopleDictionary | undefined,
    key: string | null | undefined
): IPaymentsPerson | null => (key ? (people?.[key] ?? null) : null);

export interface IPaymentsDashboard {
    /** Data italiana di riferimento del calcolo. */
    generated_on: string;
    due_window_days: number;
    kpis: IPaymentsKpis;
    /** Anagrafiche citate dalle tre liste, una volta sola ciascuna. */
    people: PeopleDictionary;
    due_installments: IDueInstallment[];
    due_installments_page: IPaymentsPage;
    recent_paid: IPaidInstallment[];
    itineraries: IPaymentsItinerary[];
    itineraries_page: IPaymentsPage;
}

export const DEFAULT_PAGE_SIZE = 25;

export interface PaymentsDashboardParams {
    dueWindowDays: number;
    designerId: string | null;
    itineraryId: string | null;
    clientKey: string | null;
    search: string | null;
    recentLimit: number;
    duePage: number;
    itinerariesPage: number;
    pageSize: number;
}

/**
 * Stato di incasso di un viaggio.
 *
 * Deliberatamente NON derivato da `itineraries.status`: quando esiste un piano rate
 * l'itinerario passa a 'completed' gia' al primo acconto (trigger
 * complete_itinerary_on_first_installment), quindi lo status direbbe "concluso" con
 * tre quarti dell'importo ancora da incassare.
 */
export type ItineraryPaymentState = 'settled' | 'overdue' | 'in_progress' | 'not_started';

export const itineraryPaymentState = (row: IPaymentsItinerary): ItineraryPaymentState => {
    if (row.all_paid) return 'settled';
    if (row.has_overdue) return 'overdue';
    if (row.paid_count > 0) return 'in_progress';
    return 'not_started';
};

/** Percentuale incassata, 0-100. Il totale a zero non deve produrre NaN. */
export const paidProgress = (row: IPaymentsItinerary): number => {
    const totalCents = Math.round(row.total_amount * 100);
    if (totalCents <= 0) return 0;
    return Math.min(100, Math.round((Math.round(row.paid_amount * 100) / totalCents) * 100));
};

export type { InstallmentStatus };
